import type {
  ClientComposerConfig,
  ComposerCapabilitiesDisplay,
  ComposerSuggestionPillStyle,
  ResolvedComposerConfig,
} from '@/types/composer';
import type { ClientLayout } from '@/types/theme';

type ClientWithComposer = {
  layout?: ClientLayout & {
    capabilitiesInComposerPlaceholder?: boolean;
    composer?: ClientComposerConfig;
  };
  composer?: ClientComposerConfig;
};

function clampStarterCount(n: number | undefined): number {
  if (typeof n !== 'number' || Number.isNaN(n)) return 4;
  return Math.min(6, Math.max(2, Math.round(n)));
}

function resolveCapabilitiesDisplay(
  raw: ClientComposerConfig | undefined,
  layout: ClientWithComposer['layout']
): ComposerCapabilitiesDisplay {
  if (raw?.capabilitiesDisplay) return raw.capabilitiesDisplay;
  if (layout?.capabilitiesInComposerPlaceholder === true) return 'placeholder';
  return 'menu';
}

function resolveSuggestionPillStyle(
  raw: ClientComposerConfig | undefined
): ComposerSuggestionPillStyle {
  return raw?.suggestionPillStyle === 'filled' ? 'filled' : 'outline';
}

export function resolveComposerConfig(client: ClientWithComposer): ResolvedComposerConfig {
  const layout = client.layout ?? {};
  const raw: ClientComposerConfig = { ...layout.composer, ...client.composer };

  const philosophy = raw.philosophy ?? 'context';
  const capabilitiesDisplay = resolveCapabilitiesDisplay(raw, layout);
  const chatMessageStyle = layout.chatMessageStyle ?? 'classic';

  const showCapabilitiesStrip =
    capabilitiesDisplay === 'chip' || capabilitiesDisplay === 'placeholder';

  const showCapabilitiesFooter = capabilitiesDisplay === 'footer';
  const showCapabilitiesMenu = capabilitiesDisplay === 'menu';

  return {
    style: raw.style ?? 'minimal',
    philosophy,
    starters: raw.starters ?? 'none',
    starterCount: clampStarterCount(raw.starterCount),
    capabilitiesDisplay,
    showFocusChip: raw.showFocusChip ?? false,
    showKeyboardHint: raw.showKeyboardHint ?? false,
    alignWithDocumentBand:
      raw.alignWithDocumentBand ?? chatMessageStyle === 'document',
    suggestionPillStyle: resolveSuggestionPillStyle(raw),
    showCapabilitiesStrip,
    showCapabilitiesFooter,
    showCapabilitiesMenu,
  };
}
